import { computed, Injectable, signal } from '@angular/core';
import { PostApiService } from '../api';
import { Doddle, DoddleCollection } from '../models';
import { lastValueFrom, Observable, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubscriblyStatusService } from './subscribly-status.service';

@Injectable({
  providedIn: 'root',
})
export class PostService {
  doddles = signal<Doddle[]>([]);
  datelessDoddles = signal<Doddle[]>([]);

  constructor(
    private postApiService: PostApiService,
    private subscriblyStatus: SubscriblyStatusService,
  ) {}

  allDoddles = computed(() => {
    return [...this.doddles(), ...this.datelessDoddles()];
  });

  getDoddles(
    workspaceId: string,
    dateFrom?: string,
    dateTo?: string,
  ): Observable<DoddleCollection> {
    return this.subscriblyStatus
      .withFallback(
        () =>
          this.postApiService.getDoddles(workspaceId, dateFrom, dateTo, true),
        () =>
          this.postApiService.getDoddles(workspaceId, dateFrom, dateTo, false),
      )
      .pipe(
        map((collection) => this.normalizeCollection(collection)),
        tap((newDoddles) => {
          this.doddles.update((existingDoddles) => {
            // Create a map of existing doddles by ID for quick lookup
            const existingDoddlesMap = new Map(
              existingDoddles.map((doddle) => [doddle.id, doddle]),
            );

            // Add or update doddles from the new fetch
            newDoddles.dated.forEach((newDoddle) => {
              existingDoddlesMap.set(newDoddle.id, newDoddle);
            });

            // Convert map back to array
            return Array.from(existingDoddlesMap.values());
          });

          // Merge dateless doddles instead of overwriting
          this.datelessDoddles.update((existingDatelessDoddles) => {
            const existingDatelessMap = new Map(
              existingDatelessDoddles.map((doddle) => [doddle.id, doddle]),
            );

            // Add or update dateless doddles from the new fetch
            newDoddles.undated.forEach((newDatelessDoddle) => {
              existingDatelessMap.set(newDatelessDoddle.id, newDatelessDoddle);
            });

            // Convert map back to array
            return Array.from(existingDatelessMap.values());
          });
        }),
      );
  }

  getDoddleById(doddleId: string): Observable<Doddle> {
    const doddleInPostStore = this.allDoddles().find(
      (doddle) => doddle.id === doddleId,
    );

    if (doddleInPostStore) {
      return of(this.normalizeDoddle(doddleInPostStore));
    }

    return this.subscriblyStatus
      .withFallback(
        () => this.postApiService.getDoddleById(doddleId, true),
        () => this.postApiService.getDoddleById(doddleId, false),
      )
      .pipe(
        map((doddle) => this.normalizeDoddle(doddle)),
        tap((doddle) => {
          // Remove from both stores first to avoid duplicates
          this.doddles.update((doddles) =>
            doddles.filter((item) => item.id !== doddle.id),
          );
          this.datelessDoddles.update((doddles) =>
            doddles.filter((item) => item.id !== doddle.id),
          );

          // Add to the appropriate store based on publishAt
          if (doddle.publishAt) {
            this.doddles.update((doddles) => [...doddles, doddle]);
          } else {
            this.datelessDoddles.update((doddles) => [...doddles, doddle]);
          }
        }),
      );
  }

  createDoddle(payload: Partial<Doddle>): Observable<Doddle> {
    return this.subscriblyStatus
      .withFallback(
        () => this.postApiService.createDoddle(payload, true),
        () => this.postApiService.createDoddle(payload, false),
      )
      .pipe(
        map((doddle) => this.normalizeDoddle(doddle)),
        tap((doddle) => {
          doddle.publishAt
            ? this.doddles.update((doddles) => [...doddles, doddle])
            : this.datelessDoddles.update((doddles) => [...doddles, doddle]);
        }),
      );
  }

  updateDoddle(
    doddleId: string,
    payload: Partial<Doddle>,
    patchPosts = false,
  ): Observable<Doddle> {
    return this.subscriblyStatus
      .withFallback(
        () =>
          this.postApiService.updateDoddle(doddleId, payload, patchPosts, true),
        () => this.postApiService.updateDoddle(doddleId, payload, false, false),
      )
      .pipe(
        map((doddle) => this.normalizeDoddle(doddle)),
        tap((doddle) => {
          const isNowDated = doddle.publishAt;

          // Remove from both stores first
          this.doddles.update((doddles) =>
            doddles.filter((item) => item.id !== doddle.id),
          );
          this.datelessDoddles.update((doddles) =>
            doddles.filter((item) => item.id !== doddle.id),
          );

          // Add to the appropriate store based on publishAt
          if (isNowDated) {
            this.doddles.update((doddles) => [...doddles, doddle]);
          } else {
            this.datelessDoddles.update((doddles) => [...doddles, doddle]);
          }
        }),
      );
  }

  deleteDoddle(doddleId: string): Observable<Doddle> {
    return this.postApiService.deleteDoddle(doddleId).pipe(
      tap((doddle) => {
        this.doddles.update((doddles) =>
          doddles.filter((item) => item.id !== doddle.id),
        );
        this.datelessDoddles.update((doddles) =>
          doddles.filter((item) => item.id !== doddle.id),
        );
      }),
    );
  }

  /**
   * Fetches doddles for the specified date range
   */
  async getWorkspaceDoddlesForDateRange(
    workspaceId: string,
    startDate: string,
    endDate: string,
  ): Promise<DoddleCollection> {
    try {
      return await lastValueFrom(
        this.getDoddles(workspaceId, startDate, endDate),
      );
    } catch (error) {
      console.error('Error loading doddles for the calendar:', error);
      return { dated: [], undated: [] };
    }
  }

  private normalizeCollection(collection: DoddleCollection): DoddleCollection {
    return {
      dated: (collection?.dated ?? []).map((doddle) =>
        this.normalizeDoddle(doddle),
      ),
      undated: (collection?.undated ?? []).map((doddle) =>
        this.normalizeDoddle(doddle),
      ),
    };
  }

  private normalizeDoddle(doddle: Doddle): Doddle {
    return {
      ...doddle,
      channels: doddle.channels ?? [],
      mediaItems: doddle.mediaItems ?? [],
      mediaIds: doddle.mediaIds ?? [],
    };
  }
}
